import { memo, useRef } from "react";
import { extend, useFrame, useThree } from "@react-three/fiber";
import { shaderMaterial } from "@react-three/drei";
import { DoubleSide, Vector3 } from "three";
import chroma from "chroma-js";

// Adam Karlsten
// https://adamkarlsten.com/blog/creating-shader-backgrounds/
const CreationMaterial = shaderMaterial(
  {
    time: 0,
    resolution: new Vector3(),
  },
  `
varying vec2 vUv;

void main()	{
  vUv = uv;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.);
}
`,
  `
uniform vec3 resolution;
uniform float time;
uniform float rBase;
uniform float gBase;
uniform float bBase;
uniform float speed;
uniform float ripples;
uniform bool sacred;
uniform float brightness;

void mainImage( out vec4 fragColor, in vec2 fragCoord ){
	vec3 c;
	float l, z = time;

	for(int i=0;i<3;i++) {
		vec2 uv, p = fragCoord.xy/resolution.xy;
		uv = p;
		p -= 0.5;
		p.x *= resolution.x / resolution.y;
		z += 2.0;
		l = length(p);
		uv += p / l * (sin(z / 1.) + 0.1);
    uv *= abs(sin(l * ripples - z * speed) + 0.2);
    if(sacred) {
      c[i] = .002 / length(abs(mod(uv, 1.) -.5));
    }
		c.r += brightness / length(abs(mod(uv, 1.) -.5)) * rBase;
		c.g += brightness / length(abs(mod(uv, 1.) -.5)) * gBase;
		c.b += brightness / length(abs(mod(uv, 1.) -.5)) * bBase;
	}

	// fragColor = vec4(c / l, sin(time*0.1)+1.6 / 10.);
	fragColor = vec4(c / l, 0.5);
}

void main() {
  mainImage(gl_FragColor, gl_FragCoord.xy);
}
`
);

extend({ CreationMaterial });

const ShaderObject = ({ radius = 15, features }) => {
  const { sacred, baseHue, ripples, speed } = features;
  const colorVals = chroma(`hsl(${baseHue}, 100%, 50%)`).gl();

  const mesh = useRef();

  let { size } = useThree();

  useFrame((_state, delta) => {
    mesh.current.material.uniforms = {
      ...mesh.current.material.uniforms,
      rBase: { value: colorVals[0] },
      gBase: { value: colorVals[1] },
      bBase: { value: colorVals[2] },
      speed: { value: parseFloat(speed / 20) },
      ripples: { value: parseFloat(ripples) },
      sacred: { value: sacred },
      brightness: { value: sacred ? 0.004 : 0.002 },
    };
    mesh.current.material.uniforms.time.value += delta;
  });

  const pixelDensity = Math.min(devicePixelRatio, 2);

  return (
    <>
      <mesh ref={mesh}>
        <sphereGeometry args={[radius, 128, 128]} />
        <creationMaterial
          key={CreationMaterial.key}
          resolution={[
            size.width * pixelDensity,
            size.height * pixelDensity,
            1,
          ]}
          side={DoubleSide}
          transparent
          opacity={0.8}
        />
      </mesh>
    </>
  );
};

export default memo(ShaderObject);
